/*
 * PDP style buttons (snippets/pdp-style-switcher.liquid): keep the colour across styles, and make
 * the next style page open from the cache.
 *
 * 1. Links. Once the shopper has picked a Color or Size on this page (or arrived on a ?variant=
 *    link), every style button links to that style's variant with the same Color - and the same
 *    Size when that style sells it, else its first size of that Color. A style without the Color
 *    links to its DEFAULT colour in the same Size (plain link when it lacks that Size too); that
 *    page still opens on its lifestyle photo, as a plain link does: the button carries the photo's
 *    slide id in data-ls-slide, the click stores it with the scroll entry and the script after the
 *    style block of that page shows the photo. Updated on every Color/Size change.
 *
 * 2. First image of the other style pages, warmed once this page is idle - at the exact URL and
 *    width that page will request, or the cache entry is useless. The opening slide follows
 *    snippets/pdp-gallery: a ?variant= link opens on the composite of that colour (blank + design),
 *    a plain link opens on the style's first lifestyle photo (composite of the default colour when
 *    it has none). Safari and in-app WebKit browsers run neither Speculation Rules nor
 *    <link rel="prefetch">; this part works everywhere.
 *
 * 3. HTML prefetch (Chrome/Android: Speculation Rules; <link rel="prefetch"> elsewhere; Safari
 *    ignores both), as before, re-issued when a link changes.
 */
(() => {
  if (window.pdpStyleLinks) return;
  window.pdpStyleLinks = true;

  const dataEl = document.querySelector('script[data-pdp-style-links]');
  if (!dataEl) return;
  let styles;
  try {
    styles = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  let mockupConfig = null;
  try {
    mockupConfig = JSON.parse(document.querySelector('script[data-pdp-mockup-config]')?.textContent || 'null');
  } catch {
    mockupConfig = null;
  }

  const links = Array.from(document.querySelectorAll('a.pdp-style-switcher__btn[data-style-url]'));
  if (!links.length) return;

  const saveData = !!navigator.connection?.saveData;
  const mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

  // Colour carried only after a choice on this page, or when this page was itself opened on a variant.
  let carry = /[?&]variant=/.test(location.search);

  function checkedValue(kind) {
    return document.querySelector(`variant-selects .pdp-option--${kind} input[type="radio"]:checked`)?.value ?? null;
  }

  // { href, ls }: ls = the lifestyle slide that page must open on although the link names a variant.
  function linkFor(url, style) {
    if (!carry) return { href: url, ls: null };
    const color = checkedValue('color');
    const size = checkedValue('size');
    const rows = style.variants.filter((row) => row[0] === color);
    if (rows.length) {
      const row = rows.find((r) => r[1] === size) || rows[0];
      return { href: `${url}?variant=${row[2]}`, ls: null };
    }
    const kept = size !== null && style.variants.find((r) => r[0] === style.def && r[1] === size);
    if (!kept) return { href: url, ls: null };
    return { href: `${url}?variant=${kept[2]}`, ls: style.ls?.id ? String(style.ls.id) : null };
  }

  function updateLinks() {
    let changed = false;
    links.forEach((link) => {
      const url = link.dataset.styleUrl;
      const style = styles[url];
      if (!style) return;
      const { href, ls } = linkFor(url, style);
      if (ls) {
        link.setAttribute('data-ls-slide', ls);
      } else {
        link.removeAttribute('data-ls-slide');
      }
      if (link.getAttribute('href') !== href) {
        link.setAttribute('href', href);
        changed = true;
      }
    });
    return changed;
  }

  /* ---------- 2. first image of each other style page ---------- */

  const warmed = new Set();
  const keep = [];

  // cors: the design <img> carries crossorigin="anonymous", and WebKit will not hand a CORS image
  // a response it cached from a no-cors request - the warm-up must use the same mode.
  function warmImage(srcset, sizes, cors) {
    if (!srcset || !sizes) return;
    const key = `${srcset}|${sizes}`;
    if (warmed.has(key)) return;
    warmed.add(key);
    const image = new Image();
    if (cors) image.crossOrigin = 'anonymous';
    image.fetchPriority = 'low';
    image.sizes = sizes;
    image.srcset = srcset;
    keep.push(image);
  }

  // Read from this page's own composite: the other style pages print the same attributes.
  function pageGeometry() {
    const blank = document.querySelector('[data-slide="composite"] .pdp-composite__blank');
    const design = document.querySelector('[data-slide="composite"] .pdp-composite__design');
    if (!blank) return null;
    const source = blank.parentElement?.tagName === 'PICTURE' ? blank.parentElement.querySelector('source') : null;
    const mobile = !!(source && mobileQuery?.matches);
    const widths = (mobile ? source.dataset.blankWidths : blank.dataset.blankWidths) || '';
    return {
      sizes: blank.getAttribute('sizes'),
      mobile,
      blankWidths: widths ? widths.split(',').map((w) => w.trim()) : null,
      designSrcset: design?.getAttribute('srcset') || null,
    };
  }

  // snippets/pdp-gallery `design_sizes`, for that style's print-box width.
  function designSizes(styleHandle) {
    const width = mockupConfig?.designW?.[styleHandle];
    if (!width) return null;
    const f = width / 100;
    return `(min-width: 1280px) ${Math.ceil(681 * f)}px, (min-width: 990px) calc((100vw - 114px) * 7 / 12 * ${f}), (min-width: 750px) calc((100vw - 64px) * ${f}), calc((100vw - 32px) * ${f})`;
  }

  function warmComposite(geometry, style, blankUrl) {
    if (!blankUrl || !geometry.blankWidths) return;
    warmImage(geometry.blankWidths.map((w) => `${blankUrl}&width=${w} ${w}w`).join(', '), geometry.sizes);
    warmImage(geometry.designSrcset, designSizes(style.style), true);
  }

  function warmFirstImages() {
    if (saveData) return;
    const geometry = pageGeometry();
    if (!geometry) return;
    links.forEach((link) => {
      const style = styles[link.dataset.styleUrl];
      if (!style) return;
      const href = link.getAttribute('href') || '';
      const variantId = (href.match(/[?&]variant=(\d+)/) || [])[1];
      if (variantId && link.hasAttribute('data-ls-slide') && style.ls) {
        // Opens on the lifestyle photo (see linkFor).
        warmImage(geometry.mobile ? style.ls.m : style.ls.d, geometry.sizes);
      } else if (variantId) {
        const row = style.variants.find((r) => String(r[2]) === variantId);
        warmComposite(geometry, style, row && style.colors[row[0]]);
      } else if (style.ls) {
        warmImage(geometry.mobile ? style.ls.m : style.ls.d, geometry.sizes);
      } else {
        warmComposite(geometry, style, style.colors[style.def]);
      }
    });
  }

  /* ---------- 3. HTML prefetch ---------- */

  const prefetched = new Set();
  let rulesScript = null;

  function prefetchPages() {
    if (saveData) return;
    const urls = [];
    links.forEach((link) => {
      if (link.href && urls.indexOf(link.href) === -1) urls.push(link.href);
    });
    if (!urls.length) return;
    if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
      const rules = document.createElement('script');
      rules.type = 'speculationrules';
      rules.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls, eagerness: 'immediate' }] });
      document.head.appendChild(rules);
      rulesScript?.remove();
      rulesScript = rules;
      return;
    }
    urls.forEach((url) => {
      if (prefetched.has(url)) return;
      prefetched.add(url);
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    });
  }

  /* ---------- wiring ---------- */

  let idleDone = false;
  let debounce = null;

  function afterIdle() {
    idleDone = true;
    warmFirstImages();
    prefetchPages();
  }

  function whenIdle(callback) {
    const start = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback, { timeout: 4000 });
      } else {
        setTimeout(callback, 1500);
      }
    };
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('variant-selects')) return;
    carry = true;
    if (!updateLinks() || !idleDone) return;
    // A burst of colour taps warms only where the shopper stops.
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      warmFirstImages();
      prefetchPages();
    }, 700);
  });

  updateLinks();
  whenIdle(afterIdle);
})();
